/**
 * Skateboard Setup Builder — static reference data (not product catalog —
 * that's injected by sections/skate-setup-builder.liquid from real Shopify
 * collections, same split as assets/setup-builder-data.js does for the
 * snowboard builder).
 *
 * Sources for the numbers below, and the tag decisions, are documented in
 * Skateboard Setup Builder/METADATA.md and PLAN.md.
 */
window.SkateBuilderConfig = {
  // Shoe size -> base deck width range, US men's-equivalent. Women's sizes
  // are converted with the standard US offset (womensSize - 2) before
  // lookup — see mensEquivalentShoe in skate-setup-builder.js.
  deckWidthByShoeSize: [
    { maxSize: 6, width: [7.0, 7.5] },
    { maxSize: 9, width: [7.5, 8.0] },
    { maxSize: 99, width: [8.0, 8.5] }
  ],
  // Within the base range above, technical/street styles skew narrower,
  // bowl/park/vert styles skew wider.
  deckWidthStyleSkew: {
    narrow: ["street", "beginner"],
    wide: ["park", "vert"]
  },

  // Fallback ONLY — used when a truck product is missing the
  // "Match Your Board Size_N~lo"-hi"" tag that already exists on most of
  // the real catalog (confirmed live, see METADATA.md). The tag is the
  // primary matching source; this chart (built from published brand size
  // guides) only kicks in for products that slip through untagged.
  truckChartsByBrand: {
    Independent: [
      { model: "129", min: 6.5, max: 7.5, axleWidth: 7.2 },
      { model: "139", min: 7.5, max: 8.0, axleWidth: 7.75 },
      { model: "149", min: 8.0, max: 8.25, axleWidth: 8.0 },
      { model: "159", min: 8.25, max: 8.5, axleWidth: 8.25 },
      { model: "169", min: 8.5, max: 9.0, axleWidth: 8.5 }
    ],
    Thunder: [
      { model: "143", min: 6.9, max: 7.4, axleWidth: 7.125 },
      { model: "145", min: 7.4, max: 7.8, axleWidth: 7.6 },
      { model: "147", min: 7.8, max: 8.2, axleWidth: 8.0 },
      { model: "148", min: 8.1, max: 8.4, axleWidth: 8.25 },
      { model: "149", min: 8.3, max: 8.7, axleWidth: 8.5 },
      { model: "151", min: 8.6, max: 9.0, axleWidth: 8.75 }
    ],
    Venture: [
      { model: "5.0", min: 0, max: 7.9, axleWidth: 7.75 },
      { model: "5.2", min: 7.9, max: 8.1, axleWidth: 8.0 },
      { model: "5.6", min: 8.1, max: 8.3, axleWidth: 8.2 },
      { model: "5.8", min: 8.4, max: 8.6, axleWidth: 8.5 },
      { model: "6.1", min: 8.62, max: 99, axleWidth: 8.75 }
    ],
    "Ace Classic": [
      { model: "22", min: 7.12, max: 7.75, axleWidth: 7.5 },
      { model: "33", min: 7.75, max: 8.12, axleWidth: 7.9 },
      { model: "44", min: 8.12, max: 8.5, axleWidth: 8.25 },
      { model: "55", min: 8.5, max: 9.12, axleWidth: 8.75 }
    ],
    Slappy: [
      { model: "8.0", min: 7.5, max: 8.0, axleWidth: 8.0 },
      { model: "8.25", min: 8.0, max: 8.25, axleWidth: 8.25 },
      { model: "8.5", min: 8.25, max: 8.5, axleWidth: 8.5 },
      { model: "8.75", min: 8.5, max: 8.75, axleWidth: 8.75 }
    ]
  },

  // Recommended (size mm, durometer A) ranges by riding style. The wheels
  // step's follow-up question nudges these — see wheelFollowupQuestions.
  wheelSpecsByStyle: {
    street: { size: [50, 56], durometer: [97, 101] },
    park: { size: [50, 56], durometer: [99, 101] },
    vert: { size: [52, 56], durometer: [99, 101] },
    cruiser: { size: [56, 60], durometer: [78, 87] },
    beginner: { size: [52, 56], durometer: [90, 99] },
    cruising: { size: [65, 75], durometer: [75, 80] },
    downhill: { size: [75, 85], durometer: [77, 82] },
    freeride: { size: [65, 75], durometer: [80, 86] },
    carving: { size: [65, 75], durometer: [75, 85] }
  },

  // The wheels follow-up question isn't one-size-fits-all: cruising/carving
  // ask "speed vs control" (shifts size). Downhill riders aren't choosing
  // speed vs control (that's the whole style) — they're choosing run type.
  // Freeride's real axis is grip vs. slide (durometer), not size.
  wheelFollowupQuestions: {
    skate: {
      key: "roughness", label: "What surfaces do you skate most?",
      options: [
        { value: "smooth", label: "Smooth park/street" },
        { value: "rough", label: "Rough streets / sidewalk cracks" }
      ]
    },
    cruising: {
      key: "speedPriority", label: "Priority for this ride:",
      options: [
        { value: "speed", label: "Top speed / stability" },
        { value: "control", label: "Control / slower cruising" }
      ]
    },
    carving: {
      key: "speedPriority", label: "Priority for this ride:",
      options: [
        { value: "speed", label: "Top speed / stability" },
        { value: "control", label: "Control / slower cruising" }
      ]
    },
    downhill: {
      key: "trackType", label: "What kind of runs do you ride?",
      options: [
        { value: "open", label: "Open roads, top speed" },
        { value: "technical", label: "Tight, technical tracks" }
      ]
    },
    freeride: {
      key: "slideFeel", label: "How do you want your wheels to feel?",
      options: [
        { value: "slidey", label: "Easy sliding" },
        { value: "grippy", label: "Max grip" }
      ]
    }
  },

  // Longboard baseplate angle guidance shown as reasoning copy on truck
  // cards — low angle = stable (downhill/fast freeride), high angle =
  // responsive (carving/cruising).
  longboardAngleByStyle: {
    downhill: "low", freeride: "low", cruising: "high", carving: "high"
  },

  // ---- Tag matching --------------------------------------------------
  // Which live product tag maps to which internal riding-style value.
  // Mixed convention on purpose: longboard decks already have a usable
  // Style_N taxonomy for 3 of the 4 values, so those are reused exactly
  // as they exist on the live catalog rather than adding a parallel tag
  // that means the same thing. Everywhere else (skate deck style,
  // longboard truck style, and longboard freeride) is new territory, so
  // those get a fresh "riding-style-*" tag instead of trying to guess at
  // and possibly collide with the store's existing (likely app-managed)
  // Style_N numbering. See METADATA.md for the full investigation.
  styleTagMap: {
    skateDecks: {
      street: "riding-style-street",
      park: "riding-style-park",
      vert: "riding-style-vert",
      cruiser: "riding-style-cruiser",
      beginner: "riding-style-beginner"
    },
    longboardDecks: {
      cruising: "Style_1~Cruiser",
      downhill: "Style_4~Down Hill",
      carving: "Style_3~Drop Through",
      freeride: "riding-style-freeride"
    },
    longboardTrucks: {
      cruising: "riding-style-cruising",
      downhill: "riding-style-downhill",
      carving: "riding-style-carving",
      freeride: "riding-style-freeride"
    }
  },

  // Bearing tier (which price band a product falls into) is derived from
  // price, not a tag — nothing in the live catalog carries a speed/tier
  // signal (checked, see METADATA.md). Thresholds are cents, adjustable
  // without touching matching logic.
  bearingPriceThresholdsCents: { entryMax: 3000, midMax: 6000 },

  // Which tier to RECOMMEND is driven by riding style instead of asking
  // the customer to self-select a "budget" (that read as an odd, slightly
  // awkward question mid-flow - everyone wants to feel like they picked
  // "premium"). Faster/harder-riding styles get bumped to premium for
  // durability under load; casual styles default to entry. wheel-feel's
  // speed-leaning answers (see recommendWheelSpec) can still bump a style
  // up a tier - see pickBearingsStep in skate-setup-builder.js.
  bearingTierByStyle: {
    street: "mid", park: "mid", vert: "premium", cruiser: "entry", beginner: "entry",
    cruising: "entry", downhill: "premium", freeride: "premium", carving: "mid"
  },

  // Bushing tier parsing: live titles mix numeric durometer ("97A") and
  // qualitative wording ("Soft", "Medium") inconsistently — see
  // parseBushingTier in skate-setup-builder.js. These are the numeric
  // buckets used when a number IS present.
  bushingDurometerBands: { softMax: 88, mediumMax: 93 },

  // Hardware bolt length range (inches) by riser answer. Confirmed live:
  // 1" bolts suit a standard no-riser setup; risers need 1⅛"+ to clear
  // the added stack height. 1.05 as the split point keeps a plain 1"
  // bolt on the no-riser side without a boundary rounding issue.
  hardwareLengthByRiser: { no: [0, 1.05], yes: [1.05, 3] }
};
